import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Search,
  FileSearch,
  AlertTriangle,
  Link2,
  Mail,
  Users,
  Building2,
  BarChart3,
  Network,
  Package,
  Layers,
  ArrowRight,
  X,
} from 'lucide-react';
import { useUIStore } from '@/store/uiStore';
import { ROUTES } from '@/utils/constants';

/** Search result item type */
interface SearchResult {
  id: string;
  type: 'detection' | 'takedown' | 'campaign' | 'dmarc' | 'user' | 'page';
  title: string;
  description?: string;
  path: string;
  icon: React.ReactNode;
}

/** Backend /api/v1/search response item — frontend adds path + icon by type. */
interface BackendSearchItem {
  type: 'detection' | 'campaign' | 'email';
  id: string;
  title: string;
  description?: string;
}

/**
 * Map a backend search result to the UI shape (path + icon by type).
 * The backend may also emit `email` (honeypot) results; those are filtered out at
 * the call site since the honeypot UI was removed.
 */
function backendToResult(item: BackendSearchItem & { type: 'detection' | 'campaign' }): SearchResult {
  switch (item.type) {
    case 'detection':
      return {
        ...item,
        path: `${ROUTES.DETECTIONS}/${item.id}`,
        icon: <AlertTriangle className="h-5 w-5" strokeWidth={1.5} />,
      };
    case 'campaign':
      return {
        ...item,
        path: `${ROUTES.CAMPAIGNS}/${item.id}`,
        icon: <Link2 className="h-5 w-5" strokeWidth={1.5} />,
      };
  }
}

/** Quick navigation pages */
const QUICK_PAGES: SearchResult[] = [
  {
    id: 'page-dashboard',
    type: 'page',
    title: 'Dashboard',
    description: 'Overview and statistics',
    path: ROUTES.DASHBOARD,
    icon: <BarChart3 className="h-5 w-5" strokeWidth={1.5} />,
  },
  {
    id: 'page-detections',
    type: 'page',
    title: 'Detections',
    description: 'View and manage detected threats',
    path: ROUTES.DETECTIONS,
    icon: <FileSearch className="h-5 w-5" strokeWidth={1.5} />,
  },
  {
    id: 'page-campaigns',
    type: 'page',
    title: 'Campaigns',
    description: 'Correlated operations — where every intel signal comes together',
    path: ROUTES.CAMPAIGNS,
    icon: <Layers className="h-5 w-5" strokeWidth={1.5} />,
  },
  {
    id: 'page-intelligence',
    type: 'page',
    title: 'Intelligence',
    description: 'Overview of botnets, threat actors and the recovered-kit corpus',
    path: ROUTES.INTELLIGENCE,
    icon: <Network className="h-5 w-5" strokeWidth={1.5} />,
  },
  {
    id: 'page-botnets',
    type: 'page',
    title: 'Botnets',
    description: 'Attacker sending and hosting infrastructure',
    path: ROUTES.BOTNETS,
    icon: <Network className="h-5 w-5" strokeWidth={1.5} />,
  },
  {
    id: 'page-kits',
    type: 'page',
    title: 'Phishing kits',
    description: 'Recovered kit archives and their adversary infrastructure',
    path: ROUTES.KITS,
    icon: <Package className="h-5 w-5" strokeWidth={1.5} />,
  },
  {
    id: 'page-actors',
    type: 'page',
    title: 'Threat actors',
    description: 'Hard identifiers and how far each one reaches',
    path: ROUTES.ACTORS,
    icon: <Users className="h-5 w-5" strokeWidth={1.5} />,
  },
  {
    id: 'page-takedowns',
    type: 'page',
    title: 'Takedowns',
    description: 'Manage takedown requests',
    path: ROUTES.TAKEDOWNS,
    icon: <AlertTriangle className="h-5 w-5" strokeWidth={1.5} />,
  },
  {
    id: 'page-dmarc-reports',
    type: 'page',
    title: 'DMARC Reports',
    description: 'Email authentication reports',
    path: ROUTES.DMARC_REPORTS,
    icon: <Mail className="h-5 w-5" strokeWidth={1.5} />,
  },
  {
    id: 'page-dmarc-analysis',
    type: 'page',
    title: 'DMARC Analysis',
    description: 'Analyze DMARC data',
    path: ROUTES.DMARC_ANALYSIS,
    icon: <BarChart3 className="h-5 w-5" strokeWidth={1.5} />,
  },
  {
    id: 'page-users',
    type: 'page',
    title: 'Users',
    description: 'Manage user accounts',
    path: ROUTES.USERS,
    icon: <Users className="h-5 w-5" strokeWidth={1.5} />,
  },
  {
    id: 'page-settings',
    type: 'page',
    title: 'Company Settings',
    description: 'Configure organization settings',
    path: ROUTES.COMPANY_SETTINGS,
    icon: <Building2 className="h-5 w-5" strokeWidth={1.5} />,
  },
];

/**
 * GlobalSearch component - Command palette style search modal
 * Triggered by Cmd/Ctrl+K or clicking the search bar
 */
export function GlobalSearch() {
  const navigate = useNavigate();
  const { searchOpen, closeSearch } = useUIStore();
  const [query, setQuery] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [results, setResults] = useState<SearchResult[]>(QUICK_PAGES);
  const inputRef = useRef<HTMLInputElement>(null);
  const resultsRef = useRef<HTMLDivElement>(null);

  // Filter results based on query with debounced API search
  useEffect(() => {
    if (!query.trim()) {
      setResults(QUICK_PAGES);
      setSelectedIndex(0);
      return;
    }

    const lowerQuery = query.toLowerCase();
    const filtered = QUICK_PAGES.filter(
      (page) =>
        page.title.toLowerCase().includes(lowerQuery) ||
        page.description?.toLowerCase().includes(lowerQuery)
    );

    // Show page results immediately
    setResults(filtered);
    setSelectedIndex(0);

    // Debounced unified backend search across detections, campaigns, emails.
    // Min length 2 matches the backend MIN_QUERY_LEN; below that the server
    // returns an empty list anyway, no point spending the round-trip.
    if (query.trim().length >= 2) {
      const timer = setTimeout(async () => {
        try {
          const { api } = await import('@/services/api');
          const res = await api.get<{ items: BackendSearchItem[] }>('/api/v1/search', {
            params: { q: query.trim(), limit_per_type: 5 },
          });
          const apiResults = (res.data?.items ?? [])
            .filter(
              (item): item is BackendSearchItem & { type: 'detection' | 'campaign' } =>
                item.type !== 'email'
            )
            .map(backendToResult);
          setResults([...filtered, ...apiResults]);
        } catch {
          // API search failed — keep the page results we already showed.
        }
      }, 300);

      return () => clearTimeout(timer);
    }
  }, [query]);

  // Focus input when modal opens
  useEffect(() => {
    if (searchOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [searchOpen]);

  // Reset state when closing
  useEffect(() => {
    if (!searchOpen) {
      setQuery('');
      setSelectedIndex(0);
    }
  }, [searchOpen]);

  // Scroll selected item into view
  useEffect(() => {
    if (resultsRef.current) {
      const selectedElement = resultsRef.current.querySelector(`[data-index="${selectedIndex}"]`);
      if (selectedElement) {
        selectedElement.scrollIntoView({ block: 'nearest' });
      }
    }
  }, [selectedIndex]);

  /**
   * Handle keyboard navigation
   */
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setSelectedIndex((prev) => Math.min(prev + 1, results.length - 1));
          break;
        case 'ArrowUp':
          e.preventDefault();
          setSelectedIndex((prev) => Math.max(prev - 1, 0));
          break;
        case 'Enter':
          e.preventDefault();
          if (results[selectedIndex]) {
            navigate(results[selectedIndex].path);
            closeSearch();
          }
          break;
        case 'Escape':
          e.preventDefault();
          closeSearch();
          break;
      }
    },
    [results, selectedIndex, navigate, closeSearch]
  );

  /**
   * Handle result click
   */
  const handleResultClick = (result: SearchResult) => {
    navigate(result.path);
    closeSearch();
  };

  if (!searchOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-50 bg-black/50 backdrop-blur-phish-nav"
        onClick={closeSearch}
      />

      {/* Modal */}
      <div className="fixed inset-0 z-50 flex items-start justify-center pt-[15vh] px-4">
        <div
          className="w-full max-w-2xl overflow-hidden rounded-phish-lg border border-phish-border bg-phish-bg-2 shadow-none"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Search Input */}
          <div className="flex items-center gap-3 border-b border-phish-border px-4 py-3">
            <Search className="h-5 w-5 shrink-0 text-phish-fg-3" strokeWidth={1.5} />
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Search for pages, detections, campaigns..."
              className="flex-1 bg-transparent font-mono text-base text-phish-fg-1 outline-none placeholder:text-phish-fg-3"
              autoComplete="off"
              autoCorrect="off"
              autoCapitalize="off"
              spellCheck={false}
            />
            <button
              onClick={closeSearch}
              className="rounded-phish p-1 text-phish-fg-3 transition-colors duration-phish hover:bg-white/5 hover:text-phish-fg-1"
            >
              <X className="h-5 w-5" strokeWidth={1.5} />
            </button>
          </div>

          {/* Results */}
          <div
            ref={resultsRef}
            className="max-h-[400px] overflow-y-auto py-2"
          >
            {results.length === 0 ? (
              <div className="px-4 py-8 text-center">
                <Search className="mx-auto mb-3 h-10 w-10 text-phish-fg-3" strokeWidth={1.5} />
                <p className="text-sm text-phish-fg-3">
                  No results found for "{query}"
                </p>
              </div>
            ) : (
              <>
                {!query.trim() && (
                  <div className="px-4 py-2">
                    <span className="font-mono text-xs uppercase tracking-phish-label text-phish-fg-3">
                      Quick Navigation
                    </span>
                  </div>
                )}
                {results.map((result, index) => (
                  <button
                    key={result.id}
                    data-index={index}
                    onClick={() => handleResultClick(result)}
                    onMouseEnter={() => setSelectedIndex(index)}
                    className={`flex w-full items-center gap-3 px-4 py-3 text-left transition-colors duration-phish ${
                      index === selectedIndex
                        ? 'bg-phish-bg-3'
                        : 'hover:bg-white/5'
                    }`}
                  >
                    <div
                      className={`shrink-0 rounded-phish p-2 ${
                        index === selectedIndex
                          ? 'border border-phish-accent text-phish-accent'
                          : 'border border-phish-border-2 text-phish-fg-3'
                      }`}
                    >
                      {result.icon}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div
                        className={`truncate font-sans text-sm font-medium ${
                          index === selectedIndex
                            ? 'text-phish-accent'
                            : 'text-phish-fg-1'
                        }`}
                      >
                        {result.title}
                      </div>
                      {result.description && (
                        <div className="truncate text-sm text-phish-fg-3">
                          {result.description}
                        </div>
                      )}
                    </div>
                    {index === selectedIndex && (
                      <ArrowRight className="h-4 w-4 shrink-0 text-phish-accent" strokeWidth={1.5} />
                    )}
                  </button>
                ))}
              </>
            )}
          </div>

          {/* Footer */}
          <div className="flex items-center gap-4 border-t border-phish-border bg-phish-bg-1 px-4 py-3">
            <div className="flex items-center gap-2 font-mono text-xs text-phish-fg-3">
              <kbd className="rounded-phish-0 border border-phish-border px-1.5 py-0.5 text-[10px]">↑</kbd>
              <kbd className="rounded-phish-0 border border-phish-border px-1.5 py-0.5 text-[10px]">↓</kbd>
              <span>Navigate</span>
            </div>
            <div className="flex items-center gap-2 font-mono text-xs text-phish-fg-3">
              <kbd className="rounded-phish-0 border border-phish-border px-1.5 py-0.5 text-[10px]">↵</kbd>
              <span>Select</span>
            </div>
            <div className="flex items-center gap-2 font-mono text-xs text-phish-fg-3">
              <kbd className="rounded-phish-0 border border-phish-border px-1.5 py-0.5 text-[10px]">Esc</kbd>
              <span>Close</span>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default GlobalSearch;
